import { z } from "zod";

/**
 * Automations (spec §13): a trigger, optional conditions, and a message to the job's customer
 * from a template with merge fields. Texts go through the same rules as any text (test numbers
 * only until go-live); email isn't set up yet, so email automations are recorded as skipped.
 */

export const AUTOMATION_TRIGGERS = ["appointment_reminder", "crew_scheduled", "invoice_sent", "payment_received", "invoice_overdue", "job_closed", "missed_call"] as const;
export type AutomationTrigger = (typeof AUTOMATION_TRIGGERS)[number];

export const AUTOMATION_TRIGGER_LABELS: Record<AutomationTrigger, string> = {
  appointment_reminder: "Before an appointment",
  crew_scheduled: "Crew scheduled (work order confirmed)",
  invoice_sent: "Invoice sent",
  payment_received: "Payment received",
  invoice_overdue: "Invoice past due",
  job_closed: "Job closed",
  missed_call: "Missed call (text them back)",
};

/** Merge fields every trigger has, and the ones only some have. */
const COMMON = ["customer.firstName", "customer.name", "company.name", "company.phone", "job.number", "job.address", "rep.name"] as const;
export const TRIGGER_FIELDS: Record<AutomationTrigger, readonly string[]> = {
  appointment_reminder: [...COMMON, "appointment.title", "appointment.date", "appointment.time"],
  crew_scheduled: [...COMMON, "crew.date", "crew.time"],
  invoice_sent: [...COMMON, "invoice.number", "invoice.balance", "invoice.dueDate", "invoice.payUrl"],
  payment_received: [...COMMON, "payment.amount", "invoice.number", "invoice.balance"],
  invoice_overdue: [...COMMON, "invoice.number", "invoice.balance", "invoice.dueDate", "invoice.payUrl"],
  job_closed: [...COMMON, "survey.url"],
  // A caller may not be a customer yet (or have a job), so only these.
  missed_call: ["customer.firstName", "customer.name", "company.name", "company.phone"],
};

export const AUTOMATION_CHANNELS = ["sms", "email"] as const;
export type AutomationChannel = (typeof AUTOMATION_CHANNELS)[number];

export interface AutomationConditions {
  /** Appointment reminder: only these types (empty: any). */
  appointmentTypes?: string[];
  /** Only jobs in these workflows (empty: any). */
  workflowIds?: string[];
  /** Appointment reminder: hours before it starts. */
  hoursBefore?: number;
  /** Invoice past due: days after the due date. */
  daysAfter?: number;
  /** Missed call: don't text if they left a voicemail. */
  skipIfVoicemail?: boolean;
}

export const automationConditionsSchema = z.object({
  appointmentTypes: z.array(z.string().max(60)).max(30).optional(),
  workflowIds: z.array(z.string().uuid()).max(30).optional(),
  hoursBefore: z.number().int().min(1).max(168).optional(),
  daysAfter: z.number().int().min(1).max(90).optional(),
  skipIfVoicemail: z.boolean().optional(),
});

export const automationInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  trigger: z.enum(AUTOMATION_TRIGGERS),
  conditions: automationConditionsSchema.default({}),
  channel: z.enum(AUTOMATION_CHANNELS),
  subject: z.string().trim().max(200).default(""),
  template: z.string().trim().min(1).max(1600),
  enabled: z.boolean().default(false),
});
export type AutomationInput = z.infer<typeof automationInputSchema>;
/** Changes: only what's sent (no defaults, so a partial change never resets the rest). */
export const automationUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  trigger: z.enum(AUTOMATION_TRIGGERS).optional(),
  conditions: automationConditionsSchema.optional(),
  channel: z.enum(AUTOMATION_CHANNELS).optional(),
  subject: z.string().trim().max(200).optional(),
  template: z.string().trim().min(1).max(1600).optional(),
  enabled: z.boolean().optional(),
});

export interface AutomationItem extends AutomationInput {
  id: string;
  conditions: AutomationConditions;
  createdAt: string;
  lastRuns: { at: string; status: AutomationRunStatus; detail: string; jobNumber: number | null }[];
}

export type AutomationRunStatus = "sent" | "skipped" | "failed";

/** Fills in {{field}}s. Fields the trigger doesn't have, or that are empty, are listed in `missing`. */
export function renderTemplate(template: string, fields: Record<string, string>): { text: string; missing: string[] } {
  const missing: string[] = [];
  const text = template.replace(/\{\{\s*([a-zA-Z.]+)\s*\}\}/g, (_, key: string) => {
    const v = fields[key];
    if (v === undefined || v === "") {
      missing.push(key);
      return "";
    }
    return v;
  });
  return { text: text.replace(/[ \t]{2,}/g, " ").trim(), missing };
}

/** Merge fields a template uses that its trigger doesn't offer. */
export function unknownFields(template: string, trigger: AutomationTrigger): string[] {
  const used = [...template.matchAll(/\{\{\s*([a-zA-Z.]+)\s*\}\}/g)].map((m) => m[1]!);
  return [...new Set(used.filter((f) => !TRIGGER_FIELDS[trigger].includes(f)))];
}

/** Added later than the rest, so it's seeded on its own. */
export const MISSED_CALL_AUTOMATION: AutomationInput = {
  name: "Missed call: text them back",
  trigger: "missed_call",
  conditions: {},
  channel: "sms",
  subject: "",
  template: "Sorry we missed your call! This is {{company.name}}. How can we help? Reply here or we'll call you back shortly.",
  enabled: false,
};

/** The automations NMT starts with, all off. */
export const DEFAULT_AUTOMATIONS: AutomationInput[] = [
  {
    name: "Appointment reminder",
    trigger: "appointment_reminder",
    conditions: { hoursBefore: 24, appointmentTypes: ["Inspection", "Sales appointment", "Adjuster meeting"] },
    channel: "sms",
    subject: "",
    template: "Hi {{customer.firstName}}, this is {{company.name}}: a reminder of your {{appointment.title}} on {{appointment.date}} at {{appointment.time}}. Questions? Call {{company.phone}}.",
    enabled: false,
  },
  {
    name: "Crew scheduled",
    trigger: "crew_scheduled",
    conditions: {},
    channel: "sms",
    subject: "",
    template: "Hi {{customer.firstName}}, your {{company.name}} crew is scheduled for {{crew.date}}, starting around {{crew.time}}. We'll let you know if the weather changes things. Questions? {{company.phone}}",
    enabled: false,
  },
  {
    name: "Invoice ready",
    trigger: "invoice_sent",
    conditions: {},
    channel: "sms",
    subject: "",
    template: "Hi {{customer.firstName}}, your invoice {{invoice.number}} from {{company.name}} for {{invoice.balance}} is ready. View and pay online: {{invoice.payUrl}}",
    enabled: false,
  },
  {
    name: "Payment receipt",
    trigger: "payment_received",
    conditions: {},
    channel: "sms",
    subject: "",
    template: "Thank you, {{customer.firstName}}! {{company.name}} received your payment of {{payment.amount}} on invoice {{invoice.number}}.",
    enabled: false,
  },
  {
    name: "Past-due invoice reminder",
    trigger: "invoice_overdue",
    conditions: { daysAfter: 7 },
    channel: "sms",
    subject: "",
    template: "Hi {{customer.firstName}}, a friendly reminder from {{company.name}}: invoice {{invoice.number}} ({{invoice.balance}}) was due {{invoice.dueDate}}. You can view and pay it here: {{invoice.payUrl}} Call {{company.phone}} with any questions.",
    enabled: false,
  },
  {
    name: "How did we do?",
    trigger: "job_closed",
    conditions: {},
    channel: "sms",
    subject: "",
    template: "Hi {{customer.firstName}}, thank you for choosing {{company.name}}! How did we do? It takes 30 seconds: {{survey.url}}",
    enabled: false,
  },
  MISSED_CALL_AUTOMATION,
];

// ── Satisfaction survey ────────────────────────────────────────────────────

export const surveyAnswerSchema = z.object({ score: z.number().int().min(1).max(5), comment: z.string().trim().max(2000).default("") });

export interface PublicSurvey {
  company: { name: string; phone: string; logoUrl: string | null; primaryColor: string };
  customerFirstName: string;
  answered: boolean;
  score: number | null;
  /** Shown after a good score, when set in Settings. */
  reviewUrl: string | null;
}

/** A score at or below this alerts the office. */
export const LOW_SURVEY_SCORE = 3;
